import { orderBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, deepStringFind, inject, NothingHere, SubTopBar, Title, toArray } from 'Mri.Web.Common';
import * as React from 'react';
import { BsCalendar } from '@react-icons/all-files/bs/BsCalendar';
import { LinkContainer } from 'react-router-bootstrap';
import { RouteComponentProps } from 'react-router-dom';
import { Input } from 'reactstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { AlertActionsStore } from '../DisplayAlerts/AlertActionsStore';
import { DisplayEventProfileItem } from './DisplayEventProfileItem';
import { DisplayEventProfileStore } from './DisplayEventProfileStore';
import { EventProfileFolderList } from './EventProfileFolderList';

export enum DisplayEventProfileViews {
    Automations = 'automations',
    Alerts = 'alerts',
}

export enum DisplayEventProfileActions {
    Create = 'create',
    Edit = 'edit',
    Clone = 'clone',
}

export interface IDisplayEventProfilesListParams {
    readonly displayEventProfileView: DisplayEventProfileViews;
}

export interface IDisplayEventProfilesListProps extends RouteComponentProps<IDisplayEventProfilesListParams> {}

@autoinject
@observer
export class DisplayEventProfilesList extends React.Component<IDisplayEventProfilesListProps> {
    @action
    public async componentDidMount() {
        this.loadAllAsync();
    }

    public render() {
        const titleView =
            this.props.match.params.displayEventProfileView == DisplayEventProfileViews.Automations
                ? 'Automations'
                : 'Alerts';
        const location = `/display-event-profiles/${this.props.match.params.displayEventProfileView}/${DisplayEventProfileActions.Create}`;
        return (
            <div className="container-fluid">
                <Title>Event Profile {titleView}</Title>

                <SubTopBar>
                    <Input
                        type="text"
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        placeholder="Search Event Profiles..."
                    />

                    <div className="mx-2 border" />

                    <LinkContainer exact to={location} className="btn btn-primary">
                        <a>
                            Create
                            <span>
                                {this.props.match.params.displayEventProfileView == DisplayEventProfileViews.Automations
                                    ? 'Automation'
                                    : 'Alert'}
                            </span>
                        </a>
                    </LinkContainer>
                </SubTopBar>

                <div className="row">
                    <div className="col-12 px-0">
                        <table className="table table-sm table-striped fixed-header table-borderless">
                            <thead className="thead">
                                <tr>
                                    <th scope="col" className="no-wrap">
                                        Account
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Name
                                    </th>
                                    {this.props.match.params.displayEventProfileView ==
                                        DisplayEventProfileViews.Alerts && (
                                        <th scope="col" className="no-wrap">
                                            Recipients
                                        </th>
                                    )}
                                    <th scope="col" className="no-wrap">
                                        Comment
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Description
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Filter
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <EventProfileFolderList
                                    title={`Internal Display ${titleView} (${this.simpleDisplayEventProfiles.length})`}
                                    displayEventProfiles={this.simpleDisplayEventProfiles}
                                    forceOpen={!!this.searchValue}
                                />
                                <EventProfileFolderList
                                    title={`Internal Filtered Display ${titleView} (${this.advancedDisplayEventProfiles.length})`}
                                    displayEventProfiles={this.advancedDisplayEventProfiles}
                                    forceOpen={!!this.searchValue}
                                />
                                {this.props.match.params.displayEventProfileView !=
                                    DisplayEventProfileViews.Automations && (
                                    <EventProfileFolderList
                                        title={`Advanced Customer ${titleView} (${this.advancedCustomerAlertProfiles.length})`}
                                        displayEventProfiles={this.advancedCustomerAlertProfiles}
                                        forceOpen={!!this.searchValue}
                                    />
                                )}
                                {this.props.match.params.displayEventProfileView !=
                                    DisplayEventProfileViews.Automations && (
                                    <EventProfileFolderList
                                        title={`Out of Warranty ${titleView} (${this.outOfWarrantyAlertProfiles.length})`}
                                        displayEventProfiles={this.outOfWarrantyAlertProfiles}
                                        forceOpen={!!this.searchValue}
                                    />
                                )}
                                {this.props.match.params.displayEventProfileView !=
                                    DisplayEventProfileViews.Automations && (
                                    <EventProfileFolderList
                                        title={`Warranty ${titleView} (${this.warrantyAlertProfiles.length})`}
                                        displayEventProfiles={this.warrantyAlertProfiles}
                                        forceOpen={!!this.searchValue}
                                    />
                                )}
                            </tbody>
                        </table>

                        <NothingHere
                            empty={!this.displayEventProfiles.length}
                            icon={<BsCalendar />}
                            loading={this.loading}
                            label="No Event Profiles"
                            loadingMessage="Loading Profiles..."
                        />
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async loadAllAsync() {
        this.loading = true;
        try {
            await this.accountsStore.loadAllAsync();
            await this.displayEventProfileStore.loadAllAsync();
            await this.alertActionsStore.loadAllAsync();
            await this.alertActionsStore.loadRecipientsAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get displayEventProfiles() {
        let displayEventProfiles = toArray(this.displayEventProfileStore.displayEventProfiles);

        if (this.searchValue)
            displayEventProfiles = displayEventProfiles.filter(p => deepStringFind(p, this.searchValue));

        if (this.props.match.params.displayEventProfileView == DisplayEventProfileViews.Automations)
            displayEventProfiles = displayEventProfiles.filter(p => p.displayEventStatus != null);

        if (this.props.match.params.displayEventProfileView == DisplayEventProfileViews.Alerts)
            displayEventProfiles = displayEventProfiles.filter(p => p.displayEventStatus == null);

        displayEventProfiles = orderBy(displayEventProfiles, [p => p.account, p => p.name]);

        return displayEventProfiles;
    }

    @computed
    private get simpleDisplayEventProfiles() {
        const automations = this.props.match.params.displayEventProfileView == DisplayEventProfileViews.Automations;

        if (automations) {
            return this.displayEventProfiles.filter(p => p.displayFilter.displays?.length && !p.account);
        }

        return this.displayEventProfiles.filter(
            p =>
                p.alertActionGuid != null &&
                p.displayFilter.displays?.length &&
                !p.account &&
                this.alertActionsStore.alertActions
                    .get(p.alertActionGuid)
                    ?.actionRecipients.filter(ar => ar.type == Mri.DisplayAlerts.RecipientType.AccountSubscribers)
                    .length === 0
        );
    }

    @computed
    private get advancedDisplayEventProfiles() {
        const automations = this.props.match.params.displayEventProfileView == DisplayEventProfileViews.Automations;

        if (automations) {
            return this.displayEventProfiles.filter(p => !p.displayFilter.displays?.length && !p.account);
        }

        return this.displayEventProfiles.filter(
            p =>
                p.alertActionGuid != null &&
                !p.displayFilter.displays?.length &&
                !p.account &&
                this.alertActionsStore.alertActions
                    .get(p.alertActionGuid)
                    ?.actionRecipients.filter(ar => ar.type == Mri.DisplayAlerts.RecipientType.AccountSubscribers)
                    .length === 0
                &&
                this.alertActionsStore.alertActions
                    .get(p.alertActionGuid)
                    ?.actionRecipients.filter(ar => ar.type == Mri.DisplayAlerts.RecipientType.OutOfWarrantySubscribers)
                    .length === 0
        );
    }

    @computed
    private get advancedCustomerAlertProfiles() {
        return this.displayEventProfiles.filter(p => p.account);
    }

    @computed
    private get warrantyAlertProfiles() {
        return this.displayEventProfiles.filter(
            p =>
                p.alertActionGuid != undefined &&
                this.alertActionsStore.alertActions
                    .get(p.alertActionGuid)
                    ?.actionRecipients.find(ar => ar.type == Mri.DisplayAlerts.RecipientType.AccountSubscribers)
        );
    }

    @computed
    private get outOfWarrantyAlertProfiles() {
        return this.displayEventProfiles.filter(
            p =>
                p.alertActionGuid != undefined &&
                this.alertActionsStore.alertActions
                    .get(p.alertActionGuid)
                    ?.actionRecipients.find(ar => ar.type == Mri.DisplayAlerts.RecipientType.OutOfWarrantySubscribers)
        );
    }

    @observable
    private loading = false;
    @observable
    private searchValue: string = '';
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private alertActionsStore!: AlertActionsStore;
    @inject
    private appStore!: AppStore;
    @inject
    private displayEventProfileStore!: DisplayEventProfileStore;
}
